import { memo, useCallback, useContext } from 'react'
import type { MouseEvent } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import OfferCard from 'components/OfferList/OfferCards/OfferCard'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import ChatCruiseAddToTripAction from './ChatCruiseAddToTripAction'
import Clickable from 'components/Common/Clickable'
import TextButton from 'LuxKit/Button/TextButton'
import useTripPlannerCTALabels from 'tripPlanner/hooks/useTripPlannerCTALabels'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import ChatHotelCarouselPrice from './ChatHotelCarouselPrice'

const HotelCardWrapper = styled.div`
  position: relative;
`

const SelectDatesButton = styled(TextButton)`
  position: relative;
  z-index: 2;
`

const ClickOverlay = styled(Clickable)`
  position: absolute;
  inset: 0;
  z-index: 1;
  cursor: pointer;
`

const AddToTripButtonContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(12)};
  z-index: 2;
`

const BookedLabelContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline-start: ${rem(12)};
  z-index: 2;
`

interface Props {
  offerId: string
  index: number
  cardType?: 'hotel' | 'experience' | 'cruise'
  offerName?: string
  offerType?: string
  offerCity?: string
  offerCoordinates?: { lat: number; lng: number }
  itineraryData?: TripItineraryData
  isBooked?: boolean
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onOfferClick?: (offerId: string) => void
  onOfferHover?: (offerId: string | undefined) => void
  offerUrl?: string
  openDatePicker?: () => void
  selectedDatesAndRoomsFilter?: App.OfferListFilters
  filterNights?: number
  trackingOffer?: App.AnyOffer
  cruiseOffer?: App.CruiseOffer
  size?: 'small' | 'medium'
  isStreaming?: boolean
  className?: string
}

function ChatHotelCarouselCard({
  offerId,
  index,
  cardType = 'hotel',
  offerName,
  offerType,
  offerCity,
  offerCoordinates,
  itineraryData,
  isBooked,
  onSendMessage,
  onOfferClick,
  onOfferHover,
  offerUrl,
  openDatePicker,
  selectedDatesAndRoomsFilter,
  filterNights,
  trackingOffer,
  cruiseOffer,
  size = 'small',
  isStreaming = false,
  className,
}: Props) {
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const { viewOfferCopy } = useTripPlannerCTALabels()

  const handleTrackedOfferClick = useCallback(() => {
    if (trackingOffer) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: trackingOffer,
        position: index,
      })
    }
  }, [dispatchOfferListEvent, index, trackingOffer])

  const handleOverlayClick = useCallback(() => {
    handleTrackedOfferClick()
    onOfferClick?.(offerId)
  }, [handleTrackedOfferClick, onOfferClick, offerId])

  const handleDatePickerClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    // CtaComponent renders inside OfferCard's own offer-link anchor, so we must
    // stop the click reaching that anchor or it'll navigate instead of opening the picker.
    event.stopPropagation()
    event.preventDefault()
    handleTrackedOfferClick()
    openDatePicker?.()
  }, [handleTrackedOfferClick, openDatePicker])

  const SelectDatesCta = useCallback(function SelectDatesCta() {
    return <SelectDatesButton kind="primary" onClick={handleDatePickerClick}>
      <FormattedMessage
        id="3sIzsG20FG0beNGBCQuq"
        defaultMessage="Select dates"
        description="Carousel price placeholder prompting the user to pick dates for an Always-On/SPP hotel whose price is date-dependent"
      />
    </SelectDatesButton>
  }, [handleDatePickerClick])

  const handleMouseEnter = useCallback(() => {
    onOfferHover?.(offerId)
  }, [onOfferHover, offerId])

  const handleMouseLeave = useCallback(() => {
    onOfferHover?.(undefined)
  }, [onOfferHover])

  const placeName = offerName || offerId

  const hasDates = !!(selectedDatesAndRoomsFilter?.checkIn && selectedDatesAndRoomsFilter?.checkOut)
  // Always-On/SPP and Bedbank hotels have no trustworthy no-date price, so withhold it
  // and prompt for dates (same treatment across both offer types).
  const isNoDatePriceWithheld = (offerType === OFFER_TYPE_ALWAYS_ON || offerType === OFFER_TYPE_BED_BANK) && !hasDates

  return (
    <HotelCardWrapper
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {!offerUrl && onOfferClick && <ClickOverlay data-testid={`chat-offer-overlay-${offerId}`} onClick={handleOverlayClick} aria-label={viewOfferCopy} />}
      {offerUrl && (
        <ClickOverlay
          data-testid={`chat-offer-overlay-${offerId}`}
          to={offerUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleTrackedOfferClick}
          aria-label={viewOfferCopy}
        />
      )}
      {isBooked && (
        <BookedLabelContainer>
          <BookedLabel />
        </BookedLabelContainer>
      )}
      {onSendMessage && cardType === 'experience' && itineraryData && !isBooked && (
        <AddToTripButtonContainer>
          <AddToTripAction
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            placeName={placeName}
            offerId={offerId}
            offerType={offerType}
            coordinates={offerCoordinates}
            variant="filled"
          />
        </AddToTripButtonContainer>
      )}
      {onSendMessage && cardType === 'hotel' && !isBooked && (
        <AddToTripButtonContainer>
          <AddHotelToTripButton
            itineraryData={itineraryData}
            hotelName={placeName}
            offerId={offerId}
            offerType={offerType}
            hotelCity={offerCity}
            hotelCoordinates={offerCoordinates}
            onSendMessage={onSendMessage}
            filterNights={filterNights}
          />
        </AddToTripButtonContainer>
      )}
      {onSendMessage && cardType === 'cruise' && itineraryData && !isBooked && (
        <AddToTripButtonContainer>
          <ChatCruiseAddToTripAction
            offerId={offerId}
            cruise={cruiseOffer}
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            isStreaming={isStreaming}
          />
        </AddToTripButtonContainer>
      )}
      <OfferCard
        id={offerId}
        idx={index}
        variant="card-default"
        size={size}
        lazyLoad={index > 1}
        selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
        className={className}
        PricingComponent={ChatHotelCarouselPrice}
        CtaComponent={isNoDatePriceWithheld ? SelectDatesCta : undefined}
      />
    </HotelCardWrapper>
  )
}

export default memo(ChatHotelCarouselCard)
